import React from 'react';
import { cn } from '../utils/cn';
import { Z_LAYERS } from '../constants/zLayers';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'default';
  children?: React.ReactNode;
}

export function Dialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  children,
}: DialogProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      style={{ zIndex: Z_LAYERS.MODAL }}
      role="presentation"
    >
      <div
        className={cn(
          'w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border',
          'border-gray-200/50 dark:border-gray-700/50',
          'overflow-hidden transform transition-all',
          'flex flex-col max-h-[90vh]'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby={description ? 'dialog-description' : undefined}
      >
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          <h3 id="dialog-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          {description && (
            <p id="dialog-description" className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {description}
            </p>
          )}
          {children}
        </div>
        <div className="shrink-0 flex items-center justify-end gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
          {cancelText && (
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md',
                'text-gray-700 dark:text-gray-300',
                'bg-white dark:bg-gray-800',
                'border border-gray-300 dark:border-gray-600',
                'hover:bg-gray-50 dark:hover:bg-gray-700',
                'focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400'
              )}
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md',
              'text-white',
              variant === 'danger'
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500',
              'focus:outline-none focus:ring-2',
              !cancelText && 'w-full' // Make button full width when no cancel button
            )}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

interface InputDialogProps extends Omit<DialogProps, 'onConfirm'> {
  onConfirm: (value: string) => void;
  initialValue?: string;
  placeholder?: string;
}

export function InputDialog({ initialValue = '', placeholder, ...props }: InputDialogProps) {
  const [value, setValue] = React.useState(initialValue);

  // The dialog is permanently mounted and only toggled via isOpen, so the
  // initial useState seed never re-applies. Re-sync each time it opens (and
  // when initialValue changes), so Rename Folder prefills the current name and
  // the previous edit's text doesn't bleed into the next one.
  React.useEffect(() => {
    if (props.isOpen) {
      setValue(initialValue);
    }
  }, [props.isOpen, initialValue]);

  return (
    <Dialog {...props} onConfirm={() => props.onConfirm(value)}>
      <div className="mt-4">
        <label htmlFor="dialog-input" className="sr-only">
          {placeholder || 'Input'}
        </label>
        <input
          id="dialog-input"
          type="text"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'w-full px-3 py-2 text-sm rounded-md',
            'bg-white dark:bg-gray-900',
            'border border-gray-300 dark:border-gray-600',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400',
            'placeholder-gray-400 dark:placeholder-gray-500'
          )}
          autoFocus
          aria-label={placeholder || 'Input'}
        />
      </div>
    </Dialog>
  );
}
